import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {variableExample} from "../model/resolution";
import type {VariableCatalogue} from "../model/types";
import {tokenFor, VARIABLE_NODE_NAME} from "../model/variableToken";

export type VariableDocumentTextMode = {kind: "tokens"} | {kind: "resolved"; catalogue: VariableCatalogue};

/**
 * The document as plain text. Every paragraph is its own line, so an empty one comes out as the
 * blank line the author left; a hard break is a line break; a variable is its token, or what a
 * reader would see for it in the catalogue's preview.
 */
export function variableDocumentText(doc: ProseMirrorNode, mode: VariableDocumentTextMode): string {
    const leafText = (node: ProseMirrorNode) => {
        if (node.type.name === "hardBreak") return "\n";
        if (node.type.name !== VARIABLE_NODE_NAME) return node.type.spec.leafText?.(node) ?? "";

        const key = node.attrs.key as string;
        const format = (node.attrs.format as string | null) ?? "";
        const fallback = node.attrs.fallback as string | null;
        if (mode.kind === "tokens") return tokenFor(key, {format, fallback: fallback ?? undefined});

        const variable = mode.catalogue.variables.find(entry => entry.key === key);
        // A key the catalogue does not know resolves to nothing, so the reader gets the chip's fallback.
        if (!variable) return fallback ?? "";

        return variableExample(variable, mode.catalogue.preview, {format, fallback: fallback ?? undefined});
    };

    return doc.textBetween(0, doc.content.size, "\n", leafText);
}

export default variableDocumentText;
